'use client';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchReferences } from '@eliadmin/core/src/api/references/references-api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ReferencesSkeleton } from './references-skeleton';
import { ReferenceCard } from './reference-card';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { Button } from '@eliadmin/core/src/components/ui/button';

const ALL_CATEGORIES_TAB = 'all';

export const ReferencesList = ({
  locale,
  categories,
}: {
  locale: string;
  categories: { id: number; name: string }[];
}) => {
  const { t } = useTranslations();
  const [activeTab, setActiveTab] = useState(ALL_CATEGORIES_TAB);
  const activeTabId = activeTab === ALL_CATEGORIES_TAB ? undefined : Number(activeTab);

  const referencesQuery = useInfiniteQuery({
    queryKey: [CacheKey.REFERENCES, activeTab],
    queryFn: async ({ pageParam = 1 }) => {
      return await fetchReferences({
        locale,
        query: {
          page: pageParam as number,
          results: 9,
          where: activeTabId ? { categories: { equals: activeTabId } } : undefined,
        },
      });
    },
    getNextPageParam: (lastPage: any) => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: 1,
  });

  const references = referencesQuery.data?.pages.flatMap((page: any) => page.data) ?? [];

  return (
    <Tabs
      value={activeTab}
      defaultValue={activeTab}
      onValueChange={value => setActiveTab(value)}
      className={cn(
        'ea-references__tabs',
        'custom-section',
        'mt-24 flex flex-col items-center justify-center gap-12',
      )}
    >
      <TabsList
        className={cn(
          'ea-references__tabs-list',
          'custom-container',
          'hide-scroll-bar',
          'mx-auto flex h-fit flex-nowrap items-start justify-start gap-3 overflow-scroll rounded-none bg-transparent p-0',
        )}
      >
        <TabsTrigger
          value={ALL_CATEGORIES_TAB}
          className={cn(
            'ea-references__tabs-trigger',
            'font-heading uppercase',
            'rounded-none border border-primary px-6 py-3 text-base md:text-lg',
          )}
        >
          {t('text1443')}
        </TabsTrigger>
        {categories.map(category => (
          <TabsTrigger
            key={category.id}
            value={String(category.id)}
            className={cn(
              'ea-references__tabs-trigger',
              'font-heading uppercase',
              'rounded-none border border-primary px-6 py-3  text-base md:text-lg',
            )}
          >
            {category.name}
          </TabsTrigger>
        ))}
      </TabsList>
      <TabsContent
        value={activeTab}
        className={cn(
          'ea-references__tabs-content',
          'custom-container',
          'grid grid-cols-1 gap-6 sm:grid-cols-2 md:gap-8 lg:grid-cols-3 xl:gap-12',
        )}
      >
        {referencesQuery.error && <p>Error: {referencesQuery.error.message}</p>}
        {referencesQuery.isLoading && <ReferencesSkeleton />}
        {referencesQuery.isSuccess && (
          <>
            {references.length === 0 ? (
              <p className="col-span-1 pt-12 text-center text-base font-bold text-foreground sm:col-span-2 sm:text-lg lg:col-span-3 lg:text-xl">
                {t('text554')}
              </p>
            ) : (
              <>
                {references.map(reference => (
                  <ReferenceCard key={reference.id} reference={reference} />
                ))}
              </>
            )}
          </>
        )}
        <div
          className={cn('mt-12 flex w-full items-center justify-center col-span-full', {
            hidden: !referencesQuery.hasNextPage,
          })}
        >
          <Button
            onClick={() => referencesQuery.fetchNextPage()}
            isLoading={referencesQuery.isFetchingNextPage}
          >
            {t('text490')} 
          </Button>
        </div>
      </TabsContent>
    </Tabs>
  );
};
